import { useMemo, useCallback } from 'react';
import { UserProject } from '../types/project.types';
import {
	AUTHENTICATION_PROVIDER,
	AuthType,
	HubRegionToRegion,
	Region,
} from '../types';
import {
	selectProjects,
	selectTeams,
	selectAuthenticationContext,
} from '../selectors/authenticationContext.selectors';
import { useSelector, useDispatch } from 'react-redux';
import {
	getProjectBySourceId,
	getProjects,
	injectProject,
} from '../actions/authenticationContext.actions';
import { AxiosError } from 'axios';
import { generateHost } from '../utils/generateHost';
import cookies from 'browser-cookies';

interface UseProjects {
	/**
	 * Whether the app is currently fetching projects or not
	 */
	isFetching: boolean;
	/**
	 * Whether the app has initialized projects or not
	 */
	isInitialized: boolean;
	/**
	 * Whether the app has initialized a project by their sourceId or not
	 */
	isProjectByIdInitialized: boolean;
	/**
	 * Fetches projects if not already initialized
	 */
	initializeProjects: () => void;
	/**
	 * Fetches a project by its sourceId if not already initialized
	 */
	initializeProjectBySourceId: () => void;
	/**
	 * The active project as defined by authenticationContext.defaultSite
	 */
	activeProject: UserProject | undefined;
	/**
	 * All projects apart from the active project
	 */
	alternativeProjects: UserProject[];
	/**
	 * Object mapping from tenantId to projectSourceId
	 */
	tenantProjectSourceMapping: {
		[k: string]: string;
	};
	/**
	 * Switches and reroutes to project based on projectSourceId
	 * @param projectSourceId
	 * @returns
	 */
	switchProject: (projectSourceId: string) => void;
	/**
	 * Fires project/inject action. Meant to cache data while user context is refetched.
	 * @param projectSourceId
	 * @returns
	 */
	injectProjectBySourceId: (projectSourceId: string) => void;
	/**
	 * List of all available projects for a user
	 */
	projects: UserProject[];
	/**
	 * Cursor for pagination
	 */
	cursor: string | null;
	/**
	 * Error if fetching projects failed
	 */
	error: AxiosError | null;
}

type ProjectSourceId = string;
type TenantId = string;

export const useProjects = (): UseProjects => {
	const dispatch = useDispatch();
	const {
		isFetching,
		isInitialized,
		cursor,
		list: projects,
		error,
		isProjectByIdInitialized,
	} = useSelector(selectProjects);
	const teams = useSelector(selectTeams);
	const { defaultSiteProjectId } = useSelector(selectAuthenticationContext);

	// TODO Figure out a better way to determine login type
	const isAuth0 =
		localStorage.getItem(AUTHENTICATION_PROVIDER) == AuthType.Auth0;

	const tenantProjectSourceMapping = useMemo(() => {
		const mappingSet = new Set<[TenantId, ProjectSourceId]>();

		teams.forEach(team => {
			if (team.projectSourceId)
				mappingSet.add([team.tenantId, team.projectSourceId]);
		});

		// Convert the Set to an object before returning
		return Object.fromEntries(mappingSet);
	}, [teams]);

	const findActiveProject = useCallback(
		(project: UserProject) => {
			return project.id === defaultSiteProjectId;
		},
		[defaultSiteProjectId],
	);

	const findProjectBySourceId = useCallback(
		(projectSourceId: string) => (project: UserProject) =>
			project.sourceId === projectSourceId,
		[],
	);

	const switchProject = (projectSourceId: string) => {
		const project = projects.find(findProjectBySourceId(projectSourceId));
		if (project) {
			const protocol = window.location.host.includes('localhost')
				? 'http'
				: 'https';

			window.location.href = `${protocol}://${
				generateHost({
					region: HubRegionToRegion[project.hubRegion] || Region.US,
				}) + `/projects/${project.sourceId}`
			}`;
		} else {
			console.warn('project with matching id does not exist');
		}
	};

	const injectProjectBySourceId = useCallback(
		(projectSourceId: string) => {
			const project = projects.find(
				findProjectBySourceId(projectSourceId),
			);
			if (!project) return;
			// setTenantId(project.organizationId);
			dispatch(injectProject(project));
		},
		[projects.length],
	);

	const initializeProjects = () => {
		const token = cookies.get('auth0.user.token');
		// Projects are not used on Auth0 and only when product picker is enabled
		if (isAuth0) return;
		// Ignore initialization attempts while fetching
		if (isInitialized || isFetching) return;
		// Wait to make request with token
		if (!token) {
			setTimeout(() => {
				initializeProjects();
			}, 200);
		} else {
			dispatch(getProjects({ initializing: true }));
		}
	};

	const initializeProjectBySourceId = () => {
		if (isProjectByIdInitialized || isFetching) return;
		// Wait if projects or context are not initialized
		if (!isInitialized || !defaultSiteProjectId) {
			setTimeout(() => {
				initializeProjectBySourceId();
			}, 200);
		} else {
			const projectSourceId =
				defaultSiteProjectId.split('project:').pop() || '';
			const existingProject = projects.find(
				findProjectBySourceId(projectSourceId),
			);
			if (!existingProject) {
				dispatch(getProjectBySourceId(projectSourceId));
			}
		}
	};

	const activeProject: UserProject | undefined = useMemo(() => {
		let project = projects.find(findActiveProject);
		if (!project && isInitialized && isProjectByIdInitialized) {
			project = projects[0];
		}
		return project;
	}, [
		findActiveProject,
		projects.length,
		defaultSiteProjectId,
		isInitialized,
		isProjectByIdInitialized,
	]);

	const alternativeProjects: UserProject[] = projects.filter(
		project => project.id !== defaultSiteProjectId,
	);

	return {
		isFetching,
		isInitialized,
		isProjectByIdInitialized,
		injectProjectBySourceId,
		initializeProjects,
		initializeProjectBySourceId,
		activeProject,
		alternativeProjects,
		tenantProjectSourceMapping,
		switchProject,
		projects,
		cursor,
		error,
	};
};
